const RETURN_PATH_KEY = 'signupRequestReturnPath';
const SUCCESS_POPUP_KEY = 'signupRequestSuccessPopup';

export type SignupReturnPath = '/' | '/cart';

export type SignupSuccessPopupPayload = {
  isFromReferral: boolean;
};

/**
 * sessionStorage throws in private-mode Safari and when the quota is exhausted. Losing the
 * return path or the popup flag is cosmetic, so every access degrades silently — these three
 * wrappers are the only place that decision lives.
 */
const safeGet = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const safeRemove = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const isCartOrCheckoutPath = (pathname: string): boolean =>
  pathname === '/cart' ||
  pathname.startsWith('/cart/') ||
  pathname === '/checkout' ||
  pathname.startsWith('/checkout/');

export const resolveSignupReturnPath = (previousPathname: string): SignupReturnPath =>
  isCartOrCheckoutPath(previousPathname) ? '/cart' : '/';

export const getSignupReturnPath = (): SignupReturnPath => {
  const value = safeGet(RETURN_PATH_KEY);
  return value === '/cart' || value === '/' ? value : '/';
};

/** Set only when key is empty — survives reload without overwriting. */
export const setSignupReturnPathIfEmpty = (previousPathname: string): void => {
  if (safeGet(RETURN_PATH_KEY) != null) {
    return;
  }
  safeSet(RETURN_PATH_KEY, resolveSignupReturnPath(previousPathname));
};

export const clearSignupReturnPath = (): void => safeRemove(RETURN_PATH_KEY);

export const consumeSignupReturnPath = (): SignupReturnPath => {
  const path = getSignupReturnPath();
  clearSignupReturnPath();
  return path;
};

export const getSignupSuccessPopup = (): SignupSuccessPopupPayload | null => {
  const raw = safeGet(SUCCESS_POPUP_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as SignupSuccessPopupPayload;
  } catch {
    return null;
  }
};

export const setSignupSuccessPopup = (payload: SignupSuccessPopupPayload): void =>
  safeSet(SUCCESS_POPUP_KEY, JSON.stringify(payload));

export const clearSignupSuccessPopup = (): void => safeRemove(SUCCESS_POPUP_KEY);
